import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { Play, Image as ImageIcon, FileText, Trash2, MessageCircle, Paperclip, Send, Download, Upload, CheckCircle2, RotateCcw } from "lucide-react";
import MarkdownEditor from "@/components/MarkdownEditor";

/* ---------- LessonBlockRenderer (read-only learner view) ---------- */
export function LessonBlockRenderer({ block, courseId, lessonId, onQuizSubmit, attempted, currentUser }) {
  switch (block.type) {
    case "text_html":
      return <RichTextRender markdown={block.payload?.markdown || ""} />;
    case "video_url":
      return <VideoUrlPlayer url={block.payload?.url || ""} title={block.payload?.title} />;
    case "video_upload":
      return <VideoAssetPlayer courseId={courseId} assetId={block.payload?.asset_id} name={block.payload?.name} mime={block.payload?.mime} />;
    case "image_gallery":
      return <ImageGalleryRender courseId={courseId} images={block.payload?.images || []} />;
    case "mcq":
      return <McqRender block={block} onSubmit={onQuizSubmit} attempted={attempted} />;
    case "short_answer":
      return <ShortAnswerRender block={block} onSubmit={onQuizSubmit} attempted={attempted} />;
    case "assignment":
      return <AssignmentLearner block={block} courseId={courseId} lessonId={lessonId} currentUser={currentUser} />;
    case "discussion":
      return <DiscussionThread block={block} courseId={courseId} lessonId={lessonId} currentUser={currentUser} />;
    default:
      return <div className="text-sm text-kb-ink-muted">Unknown block type: {block.type}</div>;
  }
}

// Markdown <img> override hoisted to module scope to avoid component remount on every render.
const RichMdImg = ({ alt = "", ...props }) => (
  <img alt={alt} {...props} loading="lazy" className="max-w-full h-auto rounded-sm border border-slate-200 my-2" />
);
const RICH_MD_COMPONENTS = { img: RichMdImg };

function RichTextRender({ markdown }) {
  return (
    <div className="prose prose-slate max-w-none prose-headings:font-serif prose-headings:text-kb-ink prose-a:text-kb-primary prose-strong:text-kb-ink" data-testid="block-text-render">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={RICH_MD_COMPONENTS}>
        {markdown || "_(empty)_"}
      </ReactMarkdown>
    </div>
  );
}

function VideoUrlPlayer({ url, title }) {
  if (!url) return <div className="text-sm text-kb-ink-muted">No URL set</div>;
  // YouTube embed
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (ytMatch) {
    return (
      <div className="aspect-video bg-black rounded-sm overflow-hidden" data-testid="block-video-yt">
        <iframe src={`https://www.youtube.com/embed/${ytMatch[1]}`} className="w-full h-full"
          title={title || "Video"} allowFullScreen frameBorder="0" />
      </div>
    );
  }
  if (vimeoMatch) {
    return (
      <div className="aspect-video bg-black rounded-sm overflow-hidden" data-testid="block-video-vimeo">
        <iframe src={`https://player.vimeo.com/video/${vimeoMatch[1]}`} className="w-full h-full"
          title={title || "Video"} allowFullScreen frameBorder="0" />
      </div>
    );
  }
  // Direct MP4
  return (
    <div className="aspect-video bg-black rounded-sm overflow-hidden" data-testid="block-video-mp4">
      <video controls className="w-full h-full" src={url} />
    </div>
  );
}

function VideoAssetPlayer({ courseId, assetId, name, mime }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!assetId) return;
    api.get(`/courses/${courseId}/assets/${assetId}`).then((r) => {
      if (cancelled) return;
      setSrc(`data:${r.data.mime || mime || "video/mp4"};base64,${r.data.content_base64}`);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [assetId, courseId, mime]);
  if (!assetId) return <div className="text-sm text-kb-ink-muted">No video uploaded</div>;
  return (
    <div className="aspect-video bg-black rounded-sm overflow-hidden" data-testid="block-video-upload">
      {src ? <video controls className="w-full h-full" src={src} /> : (
        <div className="w-full h-full flex items-center justify-center text-white text-sm">Loading {name}…</div>
      )}
    </div>
  );
}

function ImageGalleryRender({ courseId, images }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="block-image-gallery">
      {images.map((img, i) => (
        <ImageThumb key={i} courseId={courseId} img={img} />
      ))}
    </div>
  );
}

function ImageThumb({ courseId, img }) {
  const [src, setSrc] = useState(img.url || "");
  useEffect(() => {
    let cancelled = false;
    if (!img.url && img.asset_id) {
      api.get(`/courses/${courseId}/assets/${img.asset_id}`).then((r) => {
        if (cancelled) return;
        setSrc(`data:${r.data.mime || "image/png"};base64,${r.data.content_base64}`);
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [img.asset_id, courseId, img.url]);
  return (
    <figure className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
      {src ? <img src={src} alt={img.caption || ""} className="w-full h-48 object-cover" /> : <div className="h-48 flex items-center justify-center text-xs text-kb-ink-muted">Loading…</div>}
      {img.caption && <figcaption className="text-xs text-kb-ink-muted p-2">{img.caption}</figcaption>}
    </figure>
  );
}

function McqRender({ block, onSubmit, attempted }) {
  const payload = block.payload || {};
  const multi = !!payload.multi_select;
  const [selected, setSelected] = useState([]);
  const [showResult, setShowResult] = useState(null);

  const toggle = (idx) => {
    if (showResult) return;
    if (multi) {
      setSelected((s) => s.includes(idx) ? s.filter(x => x !== idx) : [...s, idx]);
    } else {
      setSelected([idx]);
    }
  };

  const submit = async () => {
    if (selected.length === 0) return;
    const result = await onSubmit?.(block.id, multi ? selected : selected[0]);
    setShowResult(result);
  };

  return (
    <div className="border border-slate-200 rounded-sm p-5 bg-white" data-testid={`block-mcq-${block.id}`}>
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">QUIZ · {multi ? "MULTI-SELECT" : "SINGLE CHOICE"}</div>
      <div className="font-serif text-lg text-kb-ink mt-2">{payload.question || "Untitled question"}</div>
      <div className="mt-4 space-y-2">
        {(payload.options || []).map((opt, i) => {
          const isSel = selected.includes(i);
          const isCorrect = showResult && showResult.correct?.includes(i);
          const isWrong = showResult && isSel && !isCorrect;
          return (
            <button key={i} type="button" onClick={() => toggle(i)}
              className={`w-full text-left text-sm border rounded-sm p-3 flex items-center gap-3 ${
                isCorrect ? "border-emerald-500 bg-emerald-50" :
                isWrong ? "border-rose-500 bg-rose-50" :
                isSel ? "border-kb-primary bg-kb-primary/5" :
                "border-slate-200 hover:border-kb-primary/50"
              }`}
              data-testid={`mcq-${block.id}-opt-${i}`}>
              <div className={`w-4 h-4 ${multi ? "rounded-sm" : "rounded-full"} border-2 ${isSel ? "border-kb-primary bg-kb-primary" : "border-slate-300"}`} />
              <span className="flex-1">{opt}</span>
            </button>
          );
        })}
      </div>
      {!showResult && (
        <button onClick={submit} disabled={selected.length === 0} className="kb-btn-primary text-sm mt-4" data-testid={`mcq-${block.id}-submit`}>
          Submit answer
        </button>
      )}
      {showResult && (
        <div className={`mt-4 p-3 rounded-sm border ${showResult.score === 100 ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          <div className="kb-mono text-[10px] uppercase tracking-wider">{showResult.score === 100 ? "CORRECT" : "INCORRECT"}</div>
          {payload.explanation && <div className="text-sm mt-1">{payload.explanation}</div>}
        </div>
      )}
    </div>
  );
}

function ShortAnswerRender({ block, onSubmit, attempted }) {
  const payload = block.payload || {};
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const hasKeywords = (payload.keywords || []).length > 0;
  const submit = async () => {
    if (!text.trim()) return;
    const r = await onSubmit?.(block.id, text);
    setResult(r);
    setSubmitted(true);
  };
  return (
    <div className="border border-slate-200 rounded-sm p-5 bg-white" data-testid={`block-short-${block.id}`}>
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">
        {hasKeywords ? "SHORT ANSWER · AUTO-GRADED" : "SHORT ANSWER · MANUAL GRADING"}
      </div>
      <div className="font-serif text-lg text-kb-ink mt-2">{payload.question || "Untitled question"}</div>
      <textarea rows={4} className="kb-input resize-none mt-3 text-sm" placeholder={payload.placeholder || "Type your answer here…"}
        value={text} onChange={(e) => setText(e.target.value)} disabled={submitted}
        data-testid={`short-${block.id}-input`} />
      {!submitted ? (
        <button onClick={submit} className="kb-btn-primary text-sm mt-3" data-testid={`short-${block.id}-submit`}>
          Submit{hasKeywords ? "" : " for review"}
        </button>
      ) : result?.auto_graded ? (
        <div className={`mt-3 p-3 rounded-sm border ${result.passed ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid={`short-${block.id}-result`}>
          <div className="kb-mono text-[10px] uppercase tracking-wider">
            {result.passed ? "PASSED" : "BELOW THRESHOLD"} · MATCHED {result.matched_keywords?.length || 0} / {result.total_keywords} KEYWORDS · {result.score}%
          </div>
          {result.matched_keywords?.length > 0 && (
            <div className="text-xs mt-1">Found: {result.matched_keywords.join(", ")}</div>
          )}
          {result.explanation && <div className="text-sm mt-2">{result.explanation}</div>}
        </div>
      ) : (
        <div className="mt-3 p-3 rounded-sm border border-sky-200 bg-sky-50 text-sky-700 text-sm">
          Submitted — your instructor will provide feedback shortly.
        </div>
      )}
    </div>
  );
}

function AssignmentLearner({ block, courseId, lessonId, currentUser }) {
  const payload = block.payload || {};
  const [submissions, setSubmissions] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);   // [{id,name,size_kb}]
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/courses/${courseId}/lessons/${lessonId}/blocks/${block.id}/submissions`);
      setSubmissions(r.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [block.id, lessonId, courseId]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const maxMb = parseInt(payload.max_file_mb || 5);
    if (f.size > maxMb * 1024 * 1024) { alert(`Max ${maxMb}MB`); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(",")[1] || "";
      try {
        const r = await api.post(`/courses/${courseId}/assets`, {
          name: f.name, mime: f.type, size_kb: Math.round(f.size / 1024),
          content_base64: b64, kind: "doc",
        });
        setFiles((s) => [...s, r.data]);
      } catch (err) { alert(err?.response?.data?.detail || "Upload failed"); }
    };
    reader.readAsDataURL(f);
    if (e.target) e.target.value = "";
  };

  const removeFile = (id) => setFiles((s) => s.filter(f => f.id !== id));

  const submit = async () => {
    if (!text.trim() && files.length === 0) { alert("Write something or attach a file"); return; }
    setBusy(true);
    try {
      await api.post(`/courses/${courseId}/lessons/${lessonId}/blocks/${block.id}/submissions`,
        { text, file_asset_ids: files.map(f => f.id) });
      setText(""); setFiles([]);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const downloadAsset = async (asset_id, name) => {
    try {
      const r = await api.get(`/courses/${courseId}/assets/${asset_id}`);
      const link = document.createElement("a");
      link.href = `data:${r.data.mime || "application/octet-stream"};base64,${r.data.content_base64}`;
      link.download = name || r.data.name;
      link.click();
    } catch (e) { alert("Download failed"); }
  };

  return (
    <div className="border border-slate-200 rounded-sm p-5 bg-white" data-testid={`block-assignment-${block.id}`}>
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">ASSIGNMENT</div>
      <div className="prose prose-slate max-w-none mt-2 text-sm">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{payload.instructions || "_(No instructions provided)_"}</ReactMarkdown>
      </div>
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-3">
        Max {payload.max_file_mb || 5} MB per file · text + attachments accepted
      </div>

      {/* Submission form */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">YOUR SUBMISSION</div>
        <textarea rows={4} className="kb-input resize-none text-sm" placeholder="Write your answer / explanation here…"
          value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
          data-testid={`assignment-${block.id}-text`} />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label className="kb-btn-outline text-xs cursor-pointer" data-testid={`assignment-${block.id}-attach`}>
            <Paperclip className="w-3.5 h-3.5" /> Attach file
            <input type="file" className="hidden" onChange={upload} />
          </label>
          {files.map(f => (
            <span key={f.id} className="kb-mono text-[10px] uppercase tracking-wider bg-slate-100 px-2 py-1 rounded-sm text-kb-ink inline-flex items-center gap-1.5">
              {f.name}
              <button onClick={() => removeFile(f.id)} className="text-rose-600 ml-1"><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <button onClick={submit} disabled={busy} className="kb-btn-primary text-sm mt-3" data-testid={`assignment-${block.id}-submit`}>
          <Upload className="w-3.5 h-3.5" /> {busy ? "Submitting…" : "Submit assignment"}
        </button>
      </div>

      {/* Past submissions */}
      <div className="mt-5">
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">PAST SUBMISSIONS</div>
        {loading ? <div className="text-xs text-kb-ink-muted">Loading…</div> :
          submissions.length === 0 ? <div className="text-xs text-kb-ink-muted">No submissions yet.</div> :
          <div className="space-y-2">
            {submissions.map(s => {
              const cls = s.status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                       : s.status === "revision_requested" ? "border-amber-200 bg-amber-50 text-amber-700"
                       : "border-sky-200 bg-sky-50 text-sky-700";
              return (
                <div key={s.id} className="border border-slate-200 rounded-sm p-3 text-xs" data-testid={`submission-${s.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="kb-mono text-[10px] text-kb-ink-light">v{s.version} · {new Date(s.created_at).toLocaleString()} · {s.user_name}</span>
                    <span className={`kb-mono text-[10px] uppercase px-2 py-0.5 rounded-sm border ${cls}`}>{s.status.replace(/_/g, " ")}{s.score !== null && s.score !== undefined ? ` · ${s.score}%` : ""}</span>
                  </div>
                  {s.text && <div className="text-kb-ink whitespace-pre-wrap mt-1">{s.text}</div>}
                  {s.file_asset_ids?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.file_asset_ids.map(aid => (
                        <button key={aid} onClick={() => downloadAsset(aid)} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary border border-slate-200 px-2 py-1 rounded-sm inline-flex items-center gap-1 hover:border-kb-primary">
                          <Download className="w-3 h-3" /> file
                        </button>
                      ))}
                    </div>
                  )}
                  {s.feedback && (
                    <div className="mt-2 text-kb-ink-muted italic">Instructor: "{s.feedback}"</div>
                  )}
                </div>
              );
            })}
          </div>}
      </div>
    </div>
  );
}

function DiscussionThread({ block, courseId, lessonId, currentUser }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/courses/${courseId}/lessons/${lessonId}/blocks/${block.id}/comments`);
      setComments(r.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);  // light polling for new comments
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [block.id, lessonId, courseId]);

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/courses/${courseId}/lessons/${lessonId}/blocks/${block.id}/comments`, { body: text.trim() });
      setText("");
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete comment?")) return;
    try {
      await api.delete(`/courses/${courseId}/lessons/${lessonId}/comments/${cid}`);
      await load();
    } catch (e) { alert("Failed"); }
  };

  return (
    <div className="border border-slate-200 rounded-sm p-5 bg-white" data-testid={`block-discussion-${block.id}`}>
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1.5">
        <MessageCircle className="w-3 h-3" /> DISCUSSION
      </div>
      <div className="mt-2 text-sm text-kb-ink">{block.payload?.prompt || "Share your thoughts with peers"}</div>

      <div className="mt-4 space-y-3 max-h-72 overflow-auto">
        {loading ? <div className="text-xs text-kb-ink-muted">Loading…</div> :
          comments.length === 0 ? <div className="text-xs text-kb-ink-muted text-center py-3">No comments yet — be the first!</div> :
          comments.map(c => {
            const mine = currentUser && c.user_id === currentUser.user_id;
            return (
              <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`comment-${c.id}`}>
                <div className={`max-w-[85%] rounded-sm p-3 ${mine ? "bg-kb-primary text-white" : "bg-slate-100 text-kb-ink"}`}>
                  {!mine && (
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-1">
                      {c.user_name} · {c.user_role}
                    </div>
                  )}
                  <div className="text-sm whitespace-pre-wrap break-words">{c.body}</div>
                  <div className={`flex items-center justify-between gap-2 mt-1 kb-mono text-[9px] uppercase tracking-wider ${mine ? "text-white/60" : "text-kb-ink-light"}`}>
                    <span>{new Date(c.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
                    {mine && (
                      <button onClick={() => remove(c.id)} className="hover:opacity-80"><Trash2 className="w-3 h-3" /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        }
      </div>

      <div className="mt-4 flex gap-2">
        <input className="kb-input text-sm flex-1" placeholder="Add a comment…"
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); post(); } }}
          data-testid={`discussion-${block.id}-input`} />
        <button onClick={post} disabled={busy || !text.trim()} className="kb-btn-primary text-sm" data-testid={`discussion-${block.id}-send`}>
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ---------- BlockEditor (author view) ---------- */
export function BlockEditor({ block, onChange, onDelete, courseId, dirty }) {
  const setPayload = (patch) => onChange({ ...block, payload: { ...(block.payload || {}), ...patch } });

  let body = null;
  switch (block.type) {
    case "text_html":
      body = (
        <MarkdownEditor value={block.payload?.markdown || ""}
          onChange={(v) => setPayload({ markdown: v })}
          placeholder="Write rich lesson content — use the toolbar to format if you don't know Markdown"
          testId="editor-text-markdown"
          courseId={courseId} />
      );
      break;
    case "video_url":
      body = (
        <div className="space-y-2">
          <input className="kb-input text-sm" placeholder="https://youtube.com/… or vimeo or direct MP4 URL"
            value={block.payload?.url || ""} onChange={(e) => setPayload({ url: e.target.value })}
            data-testid="editor-video-url" />
          <input className="kb-input text-sm" placeholder="Video title (optional)"
            value={block.payload?.title || ""} onChange={(e) => setPayload({ title: e.target.value })} />
        </div>
      );
      break;
    case "video_upload":
      body = <VideoUploadEditor block={block} setPayload={setPayload} courseId={courseId} />;
      break;
    case "image_gallery":
      body = <ImageGalleryEditor block={block} setPayload={setPayload} courseId={courseId} />;
      break;
    case "mcq":
      body = <McqEditor block={block} setPayload={setPayload} />;
      break;
    case "short_answer":
      body = (
        <div className="space-y-2">
          <input className="kb-input text-sm" placeholder="Question prompt"
            value={block.payload?.question || ""} onChange={(e) => setPayload({ question: e.target.value })}
            data-testid="editor-short-question" />
          <input className="kb-input text-sm" placeholder="Placeholder text in answer field (optional)"
            value={block.payload?.placeholder || ""} onChange={(e) => setPayload({ placeholder: e.target.value })} />
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
              KEYWORDS FOR AUTO-GRADING (comma-separated, leave empty for manual grading)
            </label>
            <input className="kb-input text-sm mt-1"
              placeholder="e.g. variance, dependent variable, null hypothesis"
              value={(block.payload?.keywords || []).join(", ")}
              onChange={(e) => setPayload({ keywords: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
              data-testid="editor-short-keywords" />
          </div>
          {(block.payload?.keywords || []).length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">PASS THRESHOLD %</label>
                <input type="number" min="0" max="100" className="kb-input text-sm mt-1"
                  value={block.payload?.min_keyword_match_pct ?? 50}
                  onChange={(e) => setPayload({ min_keyword_match_pct: (e.target.value === "" ? "" : (parseFloat(e.target.value) || 50)) })} />
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">EXPLANATION (shown after submit)</label>
                <input className="kb-input text-sm mt-1" placeholder="Optional"
                  value={block.payload?.explanation || ""}
                  onChange={(e) => setPayload({ explanation: e.target.value })} />
              </div>
            </div>
          )}
        </div>
      );
      break;
    case "assignment":
      body = (
        <div className="space-y-2">
          <MarkdownEditor value={block.payload?.instructions || ""}
            onChange={(v) => setPayload({ instructions: v })}
            placeholder="Assignment instructions in Markdown — use the toolbar to format"
            rows={5} testId="editor-assignment-instructions"
            courseId={courseId} />
          <input type="number" className="kb-input text-sm" placeholder="Max file MB" value={block.payload?.max_file_mb || 5}
            onChange={(e) => setPayload({ max_file_mb: Math.max(1, (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 5))) })} />
        </div>
      );
      break;
    case "discussion":
      body = (
        <textarea rows={3} className="kb-input resize-none text-sm" placeholder="Discussion prompt — students can comment in real-time once published"
          value={block.payload?.prompt || ""} onChange={(e) => setPayload({ prompt: e.target.value })} />
      );
      break;
    default:
      body = <div className="text-sm text-kb-ink-muted">Unknown</div>;
  }

  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white" data-testid={`block-editor-${block.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">{block.type.replace(/_/g, " ")}</div>
        <button onClick={onDelete} className="p-1 text-rose-600 hover:text-rose-700" title="Delete block" data-testid={`block-${block.id}-delete`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {body}
    </div>
  );
}

function VideoUploadEditor({ block, setPayload, courseId }) {
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { alert("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(",")[1] || "";
      const r = await api.post(`/courses/${courseId}/assets`, {
        name: f.name, mime: f.type, size_kb: Math.round(f.size / 1024),
        content_base64: b64, kind: "video",
      });
      setPayload({ asset_id: r.data.id, name: f.name, mime: f.type, size_kb: r.data.size_kb });
    };
    reader.readAsDataURL(f);
  };
  return (
    <div className="space-y-2">
      <label className="kb-btn-outline text-sm cursor-pointer w-full justify-center" data-testid="editor-video-upload-label">
        <Play className="w-3.5 h-3.5" /> {block.payload?.asset_id ? "Replace video" : "Upload MP4 (max 5MB)"}
        <input type="file" accept="video/*" className="hidden" onChange={upload} />
      </label>
      {block.payload?.asset_id && (
        <div className="kb-mono text-[10px] uppercase tracking-wider text-emerald-700">
          ✓ {block.payload.name} ({Math.round((block.payload.size_kb || 0) / 1024)}MB)
        </div>
      )}
    </div>
  );
}

function ImageGalleryEditor({ block, setPayload, courseId }) {
  const images = block.payload?.images || [];
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { alert("Max 3MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(",")[1] || "";
      const r = await api.post(`/courses/${courseId}/assets`, {
        name: f.name, mime: f.type, content_base64: b64, kind: "image",
      });
      setPayload({ images: [...images, { asset_id: r.data.id, caption: "" }] });
    };
    reader.readAsDataURL(f);
  };
  return (
    <div className="space-y-2">
      <label className="kb-btn-outline text-sm cursor-pointer w-full justify-center">
        <ImageIcon className="w-3.5 h-3.5" /> Add image (max 3MB)
        <input type="file" accept="image/*" className="hidden" onChange={upload} />
      </label>
      {images.map((img, i) => (
        <div key={i} className="flex items-center gap-2 border border-slate-200 rounded-sm p-2">
          <ImageIcon className="w-4 h-4 text-kb-ink-muted" />
          <input className="kb-input text-xs flex-1" placeholder="Caption" value={img.caption || ""}
            onChange={(e) => setPayload({ images: images.map((x, j) => j === i ? { ...x, caption: e.target.value } : x) })} />
          <button onClick={() => setPayload({ images: images.filter((_, j) => j !== i) })} className="text-rose-600 hover:text-rose-700">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function McqEditor({ block, setPayload }) {
  const opts = block.payload?.options || [];
  const correct = new Set(block.payload?.correct_options || []);
  const multi = !!block.payload?.multi_select;
  const setOpts = (newOpts) => setPayload({ options: newOpts });
  const toggleCorrect = (i) => {
    let next;
    if (multi) next = correct.has(i) ? [...correct].filter(x => x !== i) : [...correct, i];
    else next = [i];
    setPayload({ correct_options: next });
  };
  return (
    <div className="space-y-2">
      <input className="kb-input text-sm" placeholder="Question prompt"
        value={block.payload?.question || ""} onChange={(e) => setPayload({ question: e.target.value })}
        data-testid="editor-mcq-question" />
      <label className="flex items-center gap-2 text-xs text-kb-ink-muted">
        <input type="checkbox" checked={multi} onChange={(e) => setPayload({ multi_select: e.target.checked, correct_options: [] })} />
        Allow multiple correct answers
      </label>
      <div className="space-y-1.5">
        {opts.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <button type="button" onClick={() => toggleCorrect(i)}
              className={`shrink-0 w-5 h-5 ${multi ? "rounded-sm" : "rounded-full"} border-2 ${correct.has(i) ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`} title="Mark correct" />
            <input className="kb-input text-sm flex-1" placeholder={`Option ${i + 1}`} value={opt}
              onChange={(e) => setOpts(opts.map((x, j) => j === i ? e.target.value : x))} />
            <button onClick={() => setOpts(opts.filter((_, j) => j !== i))} className="p-1 text-rose-600">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button onClick={() => setOpts([...opts, ""])} className="kb-btn-outline text-xs">+ Add option</button>
      <input className="kb-input text-sm" placeholder="Explanation (shown after submit, optional)"
        value={block.payload?.explanation || ""} onChange={(e) => setPayload({ explanation: e.target.value })} />
    </div>
  );
}

export const BLOCK_TYPES = [
  { value: "text_html", label: "Rich text (Markdown)", icon: FileText },
  { value: "video_url", label: "Video (YouTube / Vimeo / URL)", icon: Play },
  { value: "video_upload", label: "Video (upload MP4)", icon: Play },
  { value: "image_gallery", label: "Image gallery", icon: ImageIcon },
  { value: "mcq", label: "Quiz · MCQ (auto-graded)", icon: FileText },
  { value: "short_answer", label: "Quiz · Short answer (manual)", icon: FileText },
  { value: "assignment", label: "Assignment", icon: FileText },
  { value: "discussion", label: "Discussion prompt", icon: MessageCircle },
];
